import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import KeyIcon from '@mui/icons-material/VpnKey'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Paper from '@mui/material/Paper'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import UploadsPanel from '../components/admin/UploadsPanel'

type SourceType = 'clicksoftware' | 'oracle_field_service' | 'servicenow_fsm' | 'sap_fsm' | 'mock' | 'custom'
type SourceStatus = 'active' | 'paused' | 'disabled'

const SOURCE_TYPE_OPTS: { value: SourceType; label: string }[] = [
  { value: 'clicksoftware', label: 'ClickSoftware' },
  { value: 'oracle_field_service', label: 'Oracle Field Service Cloud' },
  { value: 'servicenow_fsm', label: 'ServiceNow FSM' },
  { value: 'sap_fsm', label: 'SAP FSM' },
  { value: 'mock', label: 'Mock (dev/test)' },
  { value: 'custom', label: 'Custom' },
]

type Source = {
  id: string
  code: string
  display_name: string
  source_type: SourceType
  endpoint_path: string
  webhook_url: string
  hmac_secret_hint: string
  mapping: Record<string, unknown>
  status: SourceStatus
  last_event_at?: string | null
  last_test_at?: string | null
  notes: string
  created_at: string
  updated_at: string
}

type Client = ReturnType<typeof makeClient>
const QUERY_KEY = ['admin', 'sources'] as const

export default function AdminSourcesPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()

  const sourcesQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await client.callEnvelope<Source[]>('/api/v1/admin/sources')
      return data ?? []
    },
  })

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Source | null>(null)
  const [deleting, setDeleting] = useState<Source | null>(null)
  const [rotating, setRotating] = useState<Source | null>(null)

  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY })

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
            WFM Sources
          </Typography>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            Add source
          </Button>
        </Stack>

        {sourcesQ.isLoading && <Typography variant="body2">Loading…</Typography>}
        {sourcesQ.isError && (
          <Alert severity="error">{sourcesQ.error instanceof Error ? sourcesQ.error.message : 'Failed to load sources.'}</Alert>
        )}

        {sourcesQ.data && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Display name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Webhook URL</TableCell>
                  <TableCell>HMAC hint</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last event</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sourcesQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                      No WFM sources configured yet. Click <b>Add source</b> to set up your first adapter.
                    </TableCell>
                  </TableRow>
                )}
                {sourcesQ.data.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.code}</Typography>
                    </TableCell>
                    <TableCell>{s.display_name}</TableCell>
                    <TableCell><Chip label={s.source_type} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                          {s.webhook_url}
                        </Typography>
                        <Tooltip title="Copy URL">
                          <IconButton size="small" onClick={() => copyToClipboard(s.webhook_url)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.hmac_secret_hint}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.status}
                        size="small"
                        color={s.status === 'active' ? 'success' : s.status === 'paused' ? 'warning' : 'default'}
                        variant={s.status === 'active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{formatWhen(s.last_event_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Rotate HMAC secret"><IconButton size="small" onClick={() => setRotating(s)}><KeyIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleting(s)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <UploadsPanel />

      {creating && (
        <CreateSourceDialog open onClose={() => setCreating(false)} onCreated={() => { setCreating(false); invalidate() }} client={client} />
      )}
      {editing && (
        <EditSourceDialog open source={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); invalidate() }} client={client} />
      )}
      {rotating && (
        <RotateSecretDialog open source={rotating} onClose={() => setRotating(null)} onDone={() => { setRotating(null); invalidate() }} client={client} />
      )}
      {deleting && (
        <DeleteSourceDialog open source={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); invalidate() }} client={client} />
      )}
    </>
  )
}

// --- Create ----------------------------------------------------------------

function CreateSourceDialog({ open, onClose, onCreated, client }: {
  open: boolean; onClose: () => void; onCreated: () => void; client: Client
}) {
  const [code, setCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [sourceType, setSourceType] = useState<SourceType>('clicksoftware')
  const [endpointPath, setEndpointPath] = useState('')
  const [notes, setNotes] = useState('')

  const mut = useMutation({
    mutationFn: () =>
      client.call<Source>('/api/v1/admin/sources', {
        method: 'POST',
        body: {
          code: code.trim(),
          display_name: displayName.trim(),
          source_type: sourceType,
          endpoint_path: endpointPath.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      }),
    onSuccess: onCreated,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add WFM source</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" fullWidth required value={code} onChange={(e) => setCode(e.target.value)}
                     helperText="Lowercase alnum with -/_, 3–64 chars. Unique per tenant. e.g. clicksoftware_primary" />
          <TextField label="Display name" fullWidth required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <FormControl fullWidth required>
            <InputLabel id="src-type-lbl">Source type</InputLabel>
            <Select labelId="src-type-lbl" value={sourceType} label="Source type"
                    input={<OutlinedInput label="Source type" />}
                    onChange={(e: SelectChangeEvent) => setSourceType(e.target.value as SourceType)}>
              {SOURCE_TYPE_OPTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Endpoint path (optional)" fullWidth value={endpointPath} onChange={(e) => setEndpointPath(e.target.value)}
                     helperText="Defaults to the code. Appended to /api/v1/ingest/ to form the webhook URL." />
          <TextField label="Notes" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Alert severity="info" variant="outlined">
            The HMAC secret will be auto-generated and revealed once on save. Copy it into your WFM adapter — we won't show it again.
          </Alert>
          {mut.error && (
            <Alert severity="error">{mut.error instanceof ApiError ? mut.error.message : 'Create failed.'}</Alert>
          )}
          {mut.isSuccess && mut.data && <SecretRevealBlock secret={undefined} source={mut.data} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!mut.isSuccess && (
          <Button variant="contained" color="secondary" disabled={mut.isPending || !code || !displayName} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Creating…' : 'Create source'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// --- Edit ------------------------------------------------------------------

function EditSourceDialog({ open, source, onClose, onSaved, client }: {
  open: boolean; source: Source; onClose: () => void; onSaved: () => void; client: Client
}) {
  const [displayName, setDisplayName] = useState(source.display_name)
  const [status, setStatus] = useState<SourceStatus>(source.status)
  const [notes, setNotes] = useState(source.notes)
  const [mappingJson, setMappingJson] = useState(JSON.stringify(source.mapping ?? {}, null, 2))
  const [mappingError, setMappingError] = useState<string | null>(null)

  const mut = useMutation({
    mutationFn: async () => {
      let mapping: Record<string, unknown> | undefined
      try {
        mapping = JSON.parse(mappingJson) as Record<string, unknown>
      } catch {
        throw new ApiError(0, 'invalid_json', 'Mapping must be valid JSON.')
      }
      return client.call<Source>(`/api/v1/admin/sources/${source.id}`, {
        method: 'PATCH',
        body: {
          display_name: displayName.trim(),
          status,
          notes: notes.trim(),
          mapping,
        },
      })
    },
    onSuccess: onSaved,
  })

  function onMappingChange(v: string) {
    setMappingJson(v)
    try {
      JSON.parse(v)
      setMappingError(null)
    } catch (e) {
      setMappingError(e instanceof Error ? e.message : 'Invalid JSON')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit source · {source.code}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" value={source.code} disabled fullWidth />
          <TextField label="Display name" fullWidth required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <FormControl fullWidth>
            <InputLabel id="edit-status-lbl">Status</InputLabel>
            <Select labelId="edit-status-lbl" value={status} label="Status"
                    input={<OutlinedInput label="Status" />}
                    onChange={(e: SelectChangeEvent) => setStatus(e.target.value as SourceStatus)}>
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="paused">paused</MenuItem>
              <MenuItem value="disabled">disabled</MenuItem>
            </Select>
          </FormControl>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Mapping (JSON)</Typography>
            <TextField fullWidth multiline minRows={6} maxRows={16}
                       value={mappingJson}
                       onChange={(e) => onMappingChange(e.target.value)}
                       error={!!mappingError}
                       helperText={mappingError ?? 'Source-specific event mapping. Leave {} for defaults.'}
                       slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }} />
          </Box>
          <TextField label="Notes" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          {mut.error && (
            <Alert severity="error">{mut.error instanceof ApiError ? mut.error.message : 'Save failed.'}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="secondary" disabled={mut.isPending || !!mappingError || !displayName} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- Rotate secret ---------------------------------------------------------

function RotateSecretDialog({ open, source, onClose, onDone, client }: {
  open: boolean; source: Source; onClose: () => void; onDone: () => void; client: Client
}) {
  type RotateResp = { source: Source; hmac_secret_plaintext: string }
  const mut = useMutation({
    mutationFn: () => client.call<RotateResp>(`/api/v1/admin/sources/${source.id}/rotate-secret`, { method: 'POST' }),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rotate HMAC secret · {source.code}</DialogTitle>
      <DialogContent>
        {mut.data ? (
          <SecretRevealBlock secret={mut.data.hmac_secret_plaintext} source={mut.data.source} />
        ) : (
          <DialogContentText>
            Rotating invalidates the current secret immediately. The WFM adapter must be reconfigured with the new
            secret <b>before</b> its next webhook, or events will start failing HMAC verification.
          </DialogContentText>
        )}
        {mut.error && <Alert severity="error" sx={{ mt: 2 }}>{mut.error instanceof ApiError ? mut.error.message : 'Rotate failed.'}</Alert>}
      </DialogContent>
      <DialogActions>
        {mut.data ? (
          <Button variant="contained" color="secondary" onClick={onDone}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" color="warning" disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? 'Rotating…' : 'Rotate secret'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

// --- Delete ----------------------------------------------------------------

function DeleteSourceDialog({ open, source, onClose, onDone, client }: {
  open: boolean; source: Source; onClose: () => void; onDone: () => void; client: Client
}) {
  const mut = useMutation({
    mutationFn: () => client.call(`/api/v1/admin/sources/${source.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete source {source.code}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Hard-delete removes the config. Any WFM adapter still POSTing here will fail HMAC verification and be rejected.
        </DialogContentText>
        {mut.error && <Alert severity="error" sx={{ mt: 2 }}>{mut.error instanceof ApiError ? mut.error.message : 'Delete failed.'}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- Shared ----------------------------------------------------------------

function SecretRevealBlock({ secret, source }: { secret: string | undefined; source: Source }) {
  return (
    <Stack spacing={1.5}>
      <Alert severity="success" variant="outlined">Source saved. Copy the details into your WFM adapter now.</Alert>
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Webhook URL</Typography>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{source.webhook_url}</Typography>
          <IconButton size="small" onClick={() => copyToClipboard(source.webhook_url)}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      </Box>
      {secret && (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>HMAC secret (shown once)</Typography>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
              {secret}
            </Typography>
            <IconButton size="small" onClick={() => copyToClipboard(secret)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

function copyToClipboard(text: string) {
  navigator.clipboard?.writeText(text).catch(() => { /* best-effort */ })
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return '—'
  }
}
